import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Paper,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import {
  ArrowLeft,
  ArrowRight,
  Save,
  Upload,
  HelpCircle,
  Info,
  X,
} from "lucide-react";
import BreadCrumbsContainer from "../../Common/Breadcrumbs";
import { useCreateProductWithInventoryMutation } from "../../../services/baseQuery";
import { errorToast, successToast } from "../../../services/toast.service";

// Step Components
import BasicDetails from "./Components/AddProductComponents/BasicDetails";
import ProductVariants from "./Components/AddProductComponents/ProductVariants";
import PricingDetails from "./Components/AddProductComponents/PricingDetails";
import UnitConfiguration from "./Components/AddProductComponents/UnitConfiguration";
import SellAsConfiguration from "./Components/AddProductComponents/SellAsConfiguration";
import AdditionalDetails from "./Components/AddProductComponents/AdditionalDetails";
import ProductDescription from "./Components/AddProductComponents/ProductDescription";
import BatchTracking from "./Components/AddProductComponents/BatchTracking";
import InventoryManagement from "./Components/AddProductComponents/InventoryManagement";

const steps = [
  {
    label: "Basic Details",
    description: "Product information, category, brand, etc.",
    helpText:
      "Enter the essential information about your product including name, code, brand, and category.",
  },
  {
    label: "Product Variants",
    description: "Configure sizes and variants",
    helpText:
      "Set up product variants if your product comes in different sizes or configurations.",
  },
  {
    label: "Pricing",
    description: "Set MRP, discount, and selling price",
    helpText:
      "Configure pricing details including MRP, discount percentage, and selling price.",
  },
  {
    label: "Units",
    description: "Configure product units",
    helpText:
      "Define how your product is measured and sold (pieces, kg, etc.).",
  },
  {
    label: "Inventory",
    description: "Manage stock levels and warehouses",
    helpText: "Set up initial inventory levels across your warehouses.",
  },
  {
    label: "Sell As",
    description: "Configure how the product can be sold",
    helpText: "Define if the product can be sold individually or in packs.",
  },
  {
    label: "Product Description",
    description: "Add detailed product descriptions",
    helpText: "Add rich descriptions and specifications for your product.",
  },
  {
    label: "Additional Details",
    description: "Add attributes, related products, etc.",
    helpText:
      "Configure additional product details like attributes and related products.",
  },
];

const AddProduct = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [showHelpText, setShowHelpText] = useState(false);
  const [alertMessage, setAlertMessage] = useState({
    show: false,
    message: "",
    severity: "success",
  });
  const [tabValue, setTabValue] = useState(0);

  // Additional checks for productData initial state

  const validateStepWithDefaults = productData => {
    // Ensure sellAsPacks exists to prevent map errors in SellAsConfiguration component
    if (!productData.sellAsPacks) {
      productData.sellAsPacks = [];
    }

    // Ensure variants exists to prevent map errors in InventoryManagement component
    if (!productData.variants) {
      productData.variants = [];
    }

    // Ensure other array properties exist to prevent potential errors
    if (!productData.batchDetails) {
      productData.batchDetails = [];
    }

    if (!productData.inventory) {
      productData.inventory = [];
    }

    if (!productData.alternateIdentifiers) {
      productData.alternateIdentifiers = [];
    }

    if (!productData.attributes) {
      productData.attributes = [];
    }

    if (!productData.relatedProducts) {
      productData.relatedProducts = [];
    }

    if (!productData.productDescription) {
      productData.productDescription = [];
    }

    return productData;
  };

  // Initialize the productData with default values
  const initialProductData = validateStepWithDefaults({
    productCode: "SRC/2023/S101",
    name: "Elegant Chanderi Silk Salwar Suit",
    color: "Teal Blue",
    images: [],
    brand: "67c00abf0071f8fa6fb59bcd", // Assuming this is the brand ID
    description:
      "Premium Chanderi silk salwar suit with elegant embroidery and dupatta",
    categories: ["67b843782fe950652f20b688", "67b89f6b526b435cb0c52164"], // Assuming these are category IDs
    primary_category: "67b89f6b526b435cb0c52164",
    gender: "WOMEN",
    hsn_sac: "6204", // HSN for Women's or girls' salwar suits
    taxRate: 5, // 5% GST for apparel
    taxType: "EXCLUSIVE",
    productDescription: [
      {
        title: "Product Details",
        content:
          "Designer Chanderi silk salwar suit with zari embroidery work.",
      },
      {
        title: "Material Information",
        content: "Top: Chanderi Silk, Bottom: Cotton, Dupatta: Chiffon",
      },
      {
        title: "Care Instructions",
        content: "Dry clean only. Do not bleach. Iron on medium heat.",
      },
    ],
    sellCondition: {
      type: "CUSTOM_MOQ_MOV",
      minOrderQty: 3,
      minOrderValue: 5000,
      validationCondition: "ANY_ONE",
    },
    hasVariants: true,
    isSingleSize: false,
    defaultSize: "",
    availableSizes: ["S", "M", "L", "XL", "XXL"],
    batchTracking: true,
    mrp: 2499,
    discount: 12,
    sellingPrice: 2199,
    purchasePrice: 1500,
    batches: [],
    variants: [
      {
        size: "M",
        batchTracking: true,
        variantCode: "S101-M",
        pricing: {
          mrp: 2499,
          discount: 12,
          sellingPrice: 2199,
          purchasePrice: 1500,
        },
        batches: [
          {
            batchNumber: "BATCH-SS-2023-2",
            quantity: 40,
            pricing: {
              purchasePrice: 1500,
              mrp: 2499,
              discount: 12,
              sellingPrice: 2199,
            },
          },
        ],
      },
      {
        size: "L",
        batchTracking: true,
        variantCode: "S101-L",
        pricing: {
          mrp: 2599,
          discount: 12,
          sellingPrice: 2287,
          purchasePrice: 1550,
        },
        batches: [
          {
            batchNumber: "BATCH-SS-2023-3",
            quantity: 35,
            pricing: {
              purchasePrice: 1550,
              mrp: 2599,
              discount: 12,
              sellingPrice: 2287,
            },
          },
        ],
      },
      {
        size: "XL",
        batchTracking: true,
        variantCode: "S101-XL",
        pricing: {
          mrp: 2699,
          discount: 12,
          sellingPrice: 2375,
          purchasePrice: 1600,
        },
        batches: [
          {
            batchNumber: "BATCH-SS-2023-4",
            quantity: 25,
            pricing: {
              purchasePrice: 1600,
              mrp: 2699,
              discount: 12,
              sellingPrice: 2375,
            },
          },
        ],
      },
    ],
    units: {
      primaryUnit: "67c19346ed15e595797da24f", // Assuming this is the PCS unit ID
      secondaryUnit: "67fe556e6856905cf93470d2", // Assuming this is the SET unit ID
      tertiaryUnit: "",
    },
    sellAs: "PRIMARY",
    sellAsPacks: [
      {
        name: "Small Pack",
        quantity: 3,
        discountPercentage: 5,
        description: "Pack of 3 suits",
        items: [
          {
            color: "Teal Blue",
            size: "M",
            price: 2199,
            quantity: 1,
          },
          {
            color: "Teal Blue",
            size: "L",
            price: 2287,
            quantity: 1,
          },
          {
            color: "Teal Blue",
            size: "XL",
            price: 2375,
            quantity: 1,
          },
        ],
      },
      {
        name: "Retail Pack",
        quantity: 10,
        discountPercentage: 10,
        description: "Bulk pack for retailers",
        items: [
          {
            color: "Teal Blue",
            size: "M",
            price: 2199,
            quantity: 4,
          },
          {
            color: "Teal Blue",
            size: "L",
            price: 2287,
            quantity: 4,
          },
          {
            color: "Teal Blue",
            size: "XL",
            price: 2375,
            quantity: 2,
          },
        ],
      },
    ],
    alternateIdentifiers: ["8901234567890", "SSUIT-CHAND-TB-2023"],
    attributes: [
      {
        name: "Material",
        value: "Chanderi Silk",
      },
      {
        name: "Occasion",
        value: "Festive, Wedding",
      },
      {
        name: "Pattern",
        value: "Embroidered",
      },
      {
        name: "Sleeve Length",
        value: "Three-Quarter Sleeves",
      },
      {
        name: "Bottom Type",
        value: "Straight",
      },
    ],
    relatedProducts: [],
    notes:
      "Premium quality product. Limited edition festive collection. Handle with care.",
    inventory: [
      {
        warehouseId: "1", // Assuming this is the warehouse ID
        quantity: 150,
        availableQuantity: 150,
        reservedQuantity: 0,
        damagedQuantity: 0,
        minStockLevel: 30,
        maxStockLevel: 200,
        batchDetails: [],
        variant: null,
      },
      {
        warehouseId: "2", // Assuming this is another warehouse ID
        quantity: 75,
        availableQuantity: 75,
        reservedQuantity: 0,
        damagedQuantity: 0,
        minStockLevel: 20,
        maxStockLevel: 100,
        batchDetails: [],
        variant: null,
      },
    ],
  });

  const [productData, setProductData] = useState(initialProductData);

  const [errors, setErrors] = useState({});

  // Create product mutation
  const [createProduct, { isLoading: isCreatingProduct }] =
    useCreateProductWithInventoryMutation();

  const handleNext = () => {
    // Validate current step
    const isValid = validateStep(activeStep);
    if (!isValid) {
      setAlertMessage({
        show: true,
        message: "Please fix the errors before proceeding",
        severity: "error",
      });
      return;
    }

    setActiveStep(prevActiveStep => prevActiveStep + 1);
    // Auto-scroll to top when changing steps
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
    // Auto-scroll to top when changing steps
    window.scrollTo(0, 0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const validateStep = step => {
    let stepErrors = {};
    let isValid = true;

    switch (step) {
      case 0: // Basic Details
        if (!productData.productCode) {
          stepErrors.productCode = "Product code is required";
          isValid = false;
        }
        if (!productData.name) {
          stepErrors.name = "Product name is required";
          isValid = false;
        }
        if (!productData.brand) {
          stepErrors.brand = "Brand is required";
          isValid = false;
        }
        if (!productData.categories) {
          stepErrors.category = "Category is required";
          isValid = false;
        }
        break;

      case 1: // Product Variants
        if (productData.isSingleSize && !productData.defaultSize) {
          stepErrors.defaultSize = "Size is required";
          isValid = false;
        }
        if (!productData.isSingleSize && productData.variants.length === 0) {
          stepErrors.variants = "At least one variant is required";
          isValid = false;
        }
        break;

      case 2: // Pricing
        if (productData.isSingleSize && !productData.batchTracking) {
          if (!productData.mrp) {
            stepErrors.mrp = "MRP is required";
            isValid = false;
          }
          if (!productData.sellingPrice) {
            stepErrors.sellingPrice = "Selling price is required";
            isValid = false;
          }
        }
        break;

      case 3: // Units
        if (!productData.units.primaryUnit) {
          stepErrors.primaryUnit = "Primary unit is required";
          isValid = false;
        }
        break;

      // Add validation for other steps as needed

      default:
        break;
    }

    setErrors(stepErrors);
    return isValid;
  };

  const prepareProductData = () => {
    // Prepare the product data in the format expected by the API
    const formattedData = {
      productData: {
        name: productData.name,
        color: productData.color,
        images: productData.images.map(img => ({
          url: img.url,
          isDefault: img.isDefault,
        })),
        productCode: productData.productCode,
        description: productData.description,
        brand: productData.brand,
        primary_category: productData.category,
        categories: [productData.category], // Simplified for now
        gender: productData.gender,
        hsn_sac: productData.hsn_sac,
        taxRate: productData.taxRate,
        taxType: productData.taxType,
        productDescription: productData.productDescription,
        isSingleSize: productData.isSingleSize,
        defaultSize: productData.defaultSize,
        batchTracking: productData.batchTracking,
        units: {
          primary: productData.units.primaryUnit
            ? {
                _id: productData.units.primaryUnit,
              }
            : null,
          secondary: productData.units.secondaryUnit
            ? {
                _id: productData.units.secondaryUnit,
              }
            : null,
          tertiary: productData.units.tertiaryUnit
            ? {
                _id: productData.units.tertiaryUnit,
              }
            : null,
        },
        sellAs: productData.sellAs,
        alternateIdentifiers: productData.alternateIdentifiers,
        attributes: productData.attributes,
        relatedProducts: productData.relatedProducts,
        notes: productData.notes,
      },
      // Add inventory data
      inventoryData: {
        minStockLevel: 5, // Default values
        maxStockLevel: 50,
        reservedQuantity: 0,
        damagedQuantity: 0,
      },
    };

    // Add pricing data for single-size products
    if (productData.isSingleSize && !productData.batchTracking) {
      formattedData.productData.mrp = productData.mrp;
      formattedData.productData.discount = productData.discount;
      formattedData.productData.sellingPrice = productData.sellingPrice;
      formattedData.productData.purchasePrice = productData.purchasePrice;
    }

    // Add batches for single-size products with batch tracking
    if (productData.isSingleSize && productData.batchTracking) {
      formattedData.batches = productData.batches;
    }

    // Add variants for multi-size products
    if (!productData.isSingleSize) {
      formattedData.variants = productData.variants.map(variant => {
        const variantData = {
          size: variant.size,
          additionalPrice: 0, // Default value
          inventory: {
            minStockLevel: 5,
            maxStockLevel: 50,
            reservedQuantity: 0,
            damagedQuantity: 0,
          },
        };

        // Add pricing data
        variantData.mrp = variant.pricing.mrp;
        variantData.discount = variant.pricing.discount;
        variantData.sellingPrice = variant.pricing.sellingPrice;
        variantData.purchasePrice = variant.pricing.purchasePrice;

        // Add batches if batch tracking is enabled for this variant
        if (
          variant.batchTracking &&
          variant.batches &&
          variant.batches.length > 0
        ) {
          variantData.batches = variant.batches.map(batch => ({
            batchNumber: batch.batchNumber,
            initialQuantity: batch.quantity,
            purchasePrice: batch.pricing.purchasePrice,
            mrp: batch.pricing.mrp,
            discount: batch.pricing.discount,
            sellingPrice: batch.pricing.sellingPrice,
            status: "ACTIVE",
          }));
        }

        return variantData;
      });
    }

    // Add sell condition
    if (productData.sellCondition) {
      if (productData.sellCondition.type === "CUSTOM_MOQ_MOV") {
        formattedData.productData.orderValidation = {
          validationType: "MOQ_MOV",
          minOrderQty: productData.sellCondition.minOrderQty || 0,
          minOrderValue: productData.sellCondition.minOrderValue || 0,
          validationCondition:
            productData.sellCondition.validationCondition || "ANY_ONE",
        };
      } else if (productData.sellCondition.type === "FOLLOW_BRAND") {
        formattedData.productData.orderValidation = {
          validationType: "FOLLOW_BRAND",
        };
      } else {
        formattedData.productData.orderValidation = {
          validationType: "NONE",
        };
      }
    }

    return formattedData;
  };

  const handleSaveProduct = async () => {
    return;

    // Validate all steps before saving
    let allValid = true;
    let currentErrors = {};

    for (let i = 0; i < steps.length; i++) {
      const stepValid = validateStep(i);
      if (!stepValid) {
        allValid = false;
        currentErrors = { ...currentErrors, ...errors };
      }
    }

    if (!allValid) {
      setAlertMessage({
        show: true,
        message: "Please fix all errors before saving the product",
        severity: "error",
      });
      return;
    }

    try {
      // Prepare the data for API
      const formattedData = prepareProductData();

      // Call the API to create the product
      const response = await createProduct(formattedData).unwrap();

      if (response.success) {
        successToast("Product created successfully!");

        // Reset form or redirect to product list
        // window.location.href = "/products"; // Uncomment to redirect

        setAlertMessage({
          show: true,
          message: "Product saved successfully!",
          severity: "success",
        });
      } else {
        errorToast(response.message || "Failed to create product");
        setAlertMessage({
          show: true,
          message:
            "Error saving product: " + (response.message || "Unknown error"),
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error creating product:", error);
      errorToast(error.data?.error?.message || "Failed to create product");
      setAlertMessage({
        show: true,
        message:
          "Error saving product: " +
          (error.data?.error?.message || error.message || "Unknown error"),
        severity: "error",
      });
    }
  };

  const updateProductData = data => {
    setProductData(prevData => ({
      ...prevData,
      ...data,
    }));
  };

  console.log(productData, "<<< prod data asdas");

  const renderStepContent = step => {
    switch (step) {
      case 0:
        return (
          <BasicDetails
            productData={productData}
            updateProductData={updateProductData}
            errors={errors}
          />
        );
      case 1:
        return (
          <ProductVariants
            productData={productData}
            updateProductData={updateProductData}
            errors={errors}
          />
        );
      case 2:
        return productData.isSingleSize && !productData.batchTracking ? (
          <PricingDetails
            productData={productData}
            updateProductData={updateProductData}
            errors={errors}
          />
        ) : productData.isSingleSize && productData.batchTracking ? (
          <BatchTracking
            productData={productData}
            updateProductData={updateProductData}
            errors={errors}
            isSingleSize={true}
          />
        ) : (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center mb-4">
              <Info size={20} className="text-blue-500 mr-2" />
              <Typography
                variant="subtitle1"
                className="font-outfit font-medium">
                Variant Pricing Information
              </Typography>
            </div>
            <Typography className="text-gray-600 mb-4">
              Pricing is configured at the variant level. You can view and edit
              pricing details by expanding each variant in the previous step.
            </Typography>

            {/* Sell Condition Section for Variant Products */}
            <div className="mt-6 border-t pt-4">
              <Typography
                variant="subtitle2"
                className="font-outfit font-medium mb-3">
                Sell Condition
              </Typography>
              <PricingDetails
                productData={productData}
                updateProductData={updateProductData}
                errors={errors}
              />
            </div>

            <Paper elevation={0} className="bg-blue-50 p-4 rounded-lg mt-6">
              <Typography className="text-gray-700 mb-2">
                {productData.variants.some(v => v.batchTracking)
                  ? "Batch tracking is enabled for some variants. You can manage batches for each variant in the previous step by clicking the 'Add Batch' button next to the variant."
                  : "No variants have batch tracking enabled. You can enable batch tracking for variants in the previous step."}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => setActiveStep(1)}
                sx={{
                  borderColor: "#5652B0",
                  color: "#5652B0",
                  "&:hover": {
                    borderColor: "#4A47A3",
                    backgroundColor: "#F9F8FE",
                  },
                }}>
                Go Back to Variants
              </Button>
            </Paper>
          </div>
        );
      case 3:
        return (
          <UnitConfiguration
            productData={productData}
            updateProductData={updateProductData}
            errors={errors}
          />
        );
      case 4:
        return (
          <InventoryManagement
            productData={productData}
            updateProductData={updateProductData}
            errors={errors}
          />
        );
      case 5:
        return (
          <SellAsConfiguration
            productData={productData}
            updateProductData={updateProductData}
            errors={errors}
          />
        );
      case 6:
        return (
          <ProductDescription
            productData={productData}
            updateProductData={updateProductData}
            errors={errors}
          />
        );
      case 7:
        return (
          <AdditionalDetails
            productData={productData}
            updateProductData={updateProductData}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  // Bulk Import Modal
  const renderBulkImportModal = () => (
    <Dialog
      open={showBulkImportModal}
      onClose={() => setShowBulkImportModal(false)}
      maxWidth="md"
      fullWidth>
      <DialogTitle sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
        Bulk Import Products
        <IconButton
          aria-label="close"
          onClick={() => setShowBulkImportModal(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}>
          <X size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" className="mb-4">
          Upload a CSV or Excel file with your product data. Make sure your file
          follows the required format.
        </Typography>

        <Box className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-4">
          <Upload size={40} className="mx-auto mb-2 text-gray-400" />
          <Typography variant="body1" className="font-medium mb-2">
            Drag and drop your file here
          </Typography>
          <Typography variant="body2" className="text-gray-500 mb-4">
            or
          </Typography>
          <Button
            variant="outlined"
            component="label"
            sx={{
              borderColor: "#5652B0",
              color: "#5652B0",
              "&:hover": { borderColor: "#4A47A3", backgroundColor: "#F9F8FE" },
            }}>
            Browse Files
            <input type="file" hidden accept=".csv,.xlsx,.xls" />
          </Button>
        </Box>

        <Box className="bg-blue-50 p-4 rounded-lg">
          <Typography variant="body2" className="font-medium mb-2">
            Need a template?
          </Typography>
          <Button
            size="small"
            sx={{
              color: "#5652B0",
              "&:hover": { backgroundColor: "#F9F8FE" },
            }}>
            Download Template File
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setShowBulkImportModal(false)}
          sx={{
            fontFamily: "Outfit",
            color: "#5652B0",
            "&:hover": { backgroundColor: "#F9F8FE" },
          }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#5652B0",
            "&:hover": { backgroundColor: "#4A47A3" },
            fontFamily: "Outfit",
          }}>
          Import Products
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Help Text Dialog
  const renderHelpDialog = () => (
    <Dialog
      open={showHelpText}
      onClose={() => setShowHelpText(false)}
      maxWidth="sm"
      fullWidth>
      <DialogTitle sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
        {steps[activeStep].label} Help
        <IconButton
          aria-label="close"
          onClick={() => setShowHelpText(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}>
          <X size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" className="mb-4">
          {steps[activeStep].helpText}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setShowHelpText(false)}
          sx={{
            fontFamily: "Outfit",
            color: "#5652B0",
            "&:hover": { backgroundColor: "#F9F8FE" },
          }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <div className="h-[calc(100vh-120px)] bg-white">
      <BreadCrumbsContainer />
      <div className="px-6 py-4 border-b">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Add Product
          </h1>
          <button
            onClick={() => setShowBulkImportModal(true)}
            className="px-4 py-2 border border-[#5652B0] text-[#5652B0] rounded-lg text-sm font-medium hover:bg-[#F9F8FE] flex items-center gap-2">
            <Upload size={16} />
            Bulk Import
          </button>
        </div>
      </div>

      <div className="flex h-[calc(100vh-150px)]">
        {/* Left sidebar - Steps */}
        <div className="w-1/4 border-r p-4 overflow-y-auto">
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              mb: 2,
              "& .MuiTab-root": {
                fontFamily: "Outfit",
                fontSize: "0.875rem",
                textTransform: "none",
                minHeight: "40px",
              },
              "& .Mui-selected": {
                color: "#5652B0",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#5652B0",
              },
            }}>
            <Tab label="Steps" />
            <Tab label="Summary" />
          </Tabs>

          {tabValue === 0 ? (
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    optional={
                      <p className="text-xs text-gray-500">
                        {step.description}
                      </p>
                    }
                    sx={{
                      "& .MuiStepLabel-label": {
                        fontFamily: "Outfit",
                        fontWeight: index === activeStep ? 600 : 400,
                      },
                      "& .MuiStepIcon-root": {
                        color: index === activeStep ? "#5652B0" : undefined,
                      },
                    }}>
                    <span className="font-outfit">{step.label}</span>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          ) : (
            <div className="space-y-4">
              <Typography
                variant="subtitle2"
                className="font-outfit font-medium text-gray-700">
                Product Summary
              </Typography>

              <Paper elevation={0} className="p-3 bg-gray-50 rounded-lg">
                <Typography
                  variant="body2"
                  className="font-outfit font-medium text-gray-700 mb-1">
                  Basic Information
                </Typography>
                <div className="space-y-1">
                  <Typography variant="caption" className="block text-gray-600">
                    <span className="font-medium">Name:</span>{" "}
                    {productData.name || "Not set"}
                  </Typography>
                  <Typography variant="caption" className="block text-gray-600">
                    <span className="font-medium">Code:</span>{" "}
                    {productData.productCode || "Not set"}
                  </Typography>
                  <Typography variant="caption" className="block text-gray-600">
                    <span className="font-medium">Brand:</span>{" "}
                    {productData.brand || "Not set"}
                  </Typography>
                  <Typography variant="caption" className="block text-gray-600">
                    <span className="font-medium">Category:</span>{" "}
                    {productData.category || "Not set"}
                  </Typography>
                </div>
              </Paper>

              <Paper elevation={0} className="p-3 bg-gray-50 rounded-lg">
                <Typography
                  variant="body2"
                  className="font-outfit font-medium text-gray-700 mb-1">
                  Variants & Pricing
                </Typography>
                <div className="space-y-1">
                  <Typography variant="caption" className="block text-gray-600">
                    <span className="font-medium">Type:</span>{" "}
                    {productData.isSingleSize
                      ? "Single Size"
                      : "Multiple Variants"}
                  </Typography>
                  {productData.isSingleSize ? (
                    <>
                      <Typography
                        variant="caption"
                        className="block text-gray-600">
                        <span className="font-medium">Size:</span>{" "}
                        {productData.defaultSize || "Not set"}
                      </Typography>
                      <Typography
                        variant="caption"
                        className="block text-gray-600">
                        <span className="font-medium">MRP:</span> ₹
                        {productData.mrp || "0"}
                      </Typography>
                      <Typography
                        variant="caption"
                        className="block text-gray-600">
                        <span className="font-medium">Selling Price:</span> ₹
                        {productData.sellingPrice || "0"}
                      </Typography>
                    </>
                  ) : (
                    <Typography
                      variant="caption"
                      className="block text-gray-600">
                      <span className="font-medium">Variants:</span>{" "}
                      {productData.variants.length} variants configured
                    </Typography>
                  )}
                </div>
              </Paper>

              <Paper elevation={0} className="p-3 bg-gray-50 rounded-lg">
                <Typography
                  variant="body2"
                  className="font-outfit font-medium text-gray-700 mb-1">
                  Inventory & Units
                </Typography>
                <div className="space-y-1">
                  <Typography variant="caption" className="block text-gray-600">
                    <span className="font-medium">Primary Unit:</span>{" "}
                    {productData.units.primaryUnit || "Not set"}
                  </Typography>
                  <Typography variant="caption" className="block text-gray-600">
                    <span className="font-medium">Batch Tracking:</span>{" "}
                    {productData.batchTracking ? "Enabled" : "Disabled"}
                  </Typography>
                  <Typography variant="caption" className="block text-gray-600">
                    <span className="font-medium">Inventory:</span>{" "}
                    {productData.inventory.length > 0
                      ? `${productData.inventory.length} warehouses configured`
                      : "Not configured"}
                  </Typography>
                </div>
              </Paper>
            </div>
          )}
        </div>

        {/* Main content area */}
        <div className="w-3/4 p-6 overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <Typography variant="h6" className="font-outfit font-medium">
              {steps[activeStep].label}
            </Typography>
            <Tooltip title="Get help for this step">
              <IconButton
                size="small"
                onClick={() => setShowHelpText(true)}
                sx={{ color: "#5652B0" }}>
                <HelpCircle size={18} />
              </IconButton>
            </Tooltip>
          </div>

          {renderStepContent(activeStep)}

          {/* Navigation buttons */}
          <div className="flex justify-between mt-8 pt-4 border-t">
            <button
              onClick={handleBack}
              disabled={activeStep === 0}
              className={`px-6 py-2.5 rounded-lg text-base font-medium border-2 border-[#5652B0] text-[#5652B0] hover:bg-[#5652B0] hover:text-white transition-all flex items-center gap-2 ${
                activeStep === 0 ? "opacity-50 cursor-not-allowed" : ""
              }`}>
              <ArrowLeft size={16} />
              Back
            </button>

            {activeStep === steps.length - 1 ? (
              <button
                onClick={handleSaveProduct}
                disabled={isCreatingProduct}
                className="px-6 py-2.5 rounded-lg text-base font-medium text-white bg-[#5652B0] hover:bg-[#4A47A3] transition-all flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed">
                {isCreatingProduct ? (
                  <>
                    <CircularProgress size={20} color="inherit" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Product
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="px-6 py-2.5 rounded-lg text-base font-medium text-white bg-[#5652B0] hover:bg-[#4A47A3] transition-all flex items-center gap-2">
                Next
                <ArrowRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Import Modal */}
      {renderBulkImportModal()}

      {/* Help Text Dialog */}
      {renderHelpDialog()}

      {/* Alert Snackbar */}
      <Snackbar
        open={alertMessage.show}
        autoHideDuration={6000}
        onClose={() => setAlertMessage({ ...alertMessage, show: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert
          onClose={() => setAlertMessage({ ...alertMessage, show: false })}
          severity={alertMessage.severity}
          sx={{ width: "100%" }}>
          {alertMessage.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AddProduct;
